import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, SectionHeading } from "@/components/Reveal";
import api from "@/lib/api";
import { FALLBACK_BRANCHES } from "@/data/site";

const planDuration = (label) =>
  label === "Monthly" ? "1 month" : label === "Half-Yearly" ? "6 months" : "12 months";

function PlanCard({ branch, plan, featured }) {
  const save = Math.round((1 - plan.price / plan.original) * 100);
  const planId = plan.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      data-testid={`pricing-plan-${branch.slug}-${planId}`}
      className={`relative h-full clip-frame border p-8 flex flex-col transition-colors duration-300 ${
        featured ? "border-gold bg-panel2" : "border-white/10 bg-panel hover:border-gold/50"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 bg-gold text-ink px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] font-semibold">
          Best Value · Save {save}%
        </span>
      )}
      <h3 className="font-display uppercase text-2xl">{plan.label}</h3>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
        {planDuration(plan.label)} at {branch.name}
      </p>
      <p className="mt-6 flex items-baseline gap-3 flex-wrap">
        <span className="font-display text-5xl text-gold-gradient">₹{plan.price.toLocaleString("en-IN")}</span>
        <span className="text-white/40 text-sm line-through">₹{plan.original.toLocaleString("en-IN")}</span>
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
        {save}% off the regular ₹{plan.original.toLocaleString("en-IN")}
      </p>
      <ul className="mt-8 space-y-3 flex-1">
        <li className="flex gap-3 text-sm text-white/65"><span className="text-gold mt-0.5" aria-hidden="true">✦</span>Full gym floor access</li>
        <li className="flex gap-3 text-sm text-white/65"><span className="text-gold mt-0.5" aria-hidden="true">✦</span>Trainer guidance on the floor</li>
        <li className="flex gap-3 text-sm text-white/65"><span className="text-gold mt-0.5" aria-hidden="true">✦</span>Pay at reception during gym hours</li>
      </ul>
      <Link
        data-testid={`pricing-plan-${branch.slug}-${planId}-select`}
        to={`/contact?branch=${branch.slug}&plan=${plan.label}`}
        className={`mt-8 text-center px-6 py-3.5 font-bold uppercase text-xs tracking-[0.15em] clip-frame-sm transition-colors duration-300 ${
          featured
            ? "bg-gold text-ink hover:bg-gold-light"
            : "border border-white/25 text-white hover:border-gold hover:text-gold"
        }`}
      >
        Choose {plan.label}
      </Link>
    </div>
  );
}

export default function Pricing() {
  const [branches, setBranches] = useState(FALLBACK_BRANCHES);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api
      .get("/branches")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setBranches(res.data);
      })
      .catch(() => {});
  }, []);

  const b = branches[active] || branches[0];
  const yearly = b.pricing?.find((p) => p.label === "Yearly");

  return (
    <main className="pt-28 md:pt-36">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Membership Offers</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Real prices. <span className="text-gold-gradient">Zero games.</span>
          </h1>
          <p className="mt-6 max-w-xl text-white/60 text-sm md:text-base">
            Every branch runs its own offers. Pick your branch, pay directly at the reception desk
            during gym hours — no hidden fees, no card charges.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {branches.map((br, i) => (
            <button
              key={br.slug}
              data-testid={`pricing-branch-tab-${br.slug}`}
              onClick={() => setActive(i)}
              className={`px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                active === i
                  ? "bg-gold text-ink border-gold font-semibold"
                  : "border-white/15 text-white/60 hover:border-gold hover:text-gold"
              }`}
            >
              {br.name}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 md:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={b.slug}
            data-testid={`pricing-panel-${b.slug}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5 }}
            className="grid gap-6 lg:grid-cols-12 items-stretch"
          >
            <div className="lg:col-span-9 grid gap-6 sm:grid-cols-2 xl:grid-cols-3 items-stretch">
              {b.pricing.map((p) => (
                <PlanCard key={p.label} branch={b} plan={p} featured={p.label === "Yearly"} />
              ))}
            </div>
            <div className="lg:col-span-3 clip-frame border border-white/10 bg-panel p-8 flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-4">How to join</p>
              <p className="text-sm text-white/70 leading-relaxed flex-1">
                Walk in during gym hours and pay directly at the reception desk. Carry a photo ID for
                first-time registration.
              </p>
              <div className="mt-6 space-y-3 border-t border-white/10 pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{b.timings}</p>
                <a data-testid={`pricing-call-${b.slug}`} href={`tel:+${b.whatsapp}`} className="block text-sm text-white/70 hover:text-gold transition-colors">
                  Call {b.phone}
                </a>
                <a
                  data-testid={`pricing-whatsapp-${b.slug}`}
                  href={`https://wa.me/${b.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-white/70 hover:text-gold transition-colors"
                >
                  WhatsApp us →
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24 md:pb-32">
        <SectionHeading eyebrow="Compare Branches" title="Every rate, side by side." />
        <Reveal>
          <div className="overflow-x-auto clip-frame border border-white/10">
            <table data-testid="pricing-compare-table" className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="bg-panel2 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <th className="px-6 py-4">Plan</th>
                  {branches.map((br) => (
                    <th key={br.slug} className="px-6 py-4">{br.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {["Monthly", "Half-Yearly", "Yearly"].map((label) => (
                  <tr key={label} className="border-t border-white/5 hover:bg-panel transition-colors">
                    <td className="px-6 py-4 font-semibold">{label}</td>
                    {branches.map((br) => {
                      const p = br.pricing.find((x) => x.label === label);
                      return (
                        <td key={br.slug} className="px-6 py-4">
                          <span className="font-display text-xl text-white">₹{p.price.toLocaleString("en-IN")}</span>
                          <span className="ml-2 text-white/35 text-xs line-through">₹{p.original.toLocaleString("en-IN")}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
            Offer prices — regular rates struck through. Pay at the reception desk during gym hours.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <Link
            data-testid="pricing-cta-enquiry"
            to="/contact"
            className="inline-block bg-gold text-ink px-8 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
          >
            Still Have Questions? Enquire Here
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
